import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiEye, FiMessageSquare } from 'react-icons/fi';
import {
  fetchTickets,
  fetchTicketById,
  addResponse,
  updateTicketStatus,
  fetchStats,
  resetSuccess,
  clearCurrentTicket,
} from '../../store/slices/helpSupportSlice';
import { DataTable, Pagination, Modal, SearchBox } from '../../components/Common';
import { formatDate } from '../../utils/helpers';

const STATUS_OPTIONS = ['open', 'in-progress', 'resolved', 'closed'];
const CATEGORY_OPTIONS = ['account', 'subscription', 'payment', 'technical', 'report-user', 'feedback', 'other'];

const HelpSupport = () => {
  const dispatch = useDispatch();
  const { tickets, currentTicket, stats, totalTickets, totalPages, isLoading, success, error } =
    useSelector((state) => state.helpSupport);

  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [responseText, setResponseText] = useState('');

  useEffect(() => {
    const params = { page: currentPage, limit: 10 };
    if (filterStatus) params.status = filterStatus;
    if (filterCategory) params.category = filterCategory;
    if (searchTerm) params.search = searchTerm;
    dispatch(fetchTickets(params));
    dispatch(fetchStats());
  }, [dispatch, currentPage, filterStatus, filterCategory, searchTerm]);

  useEffect(() => {
    if (success) {
      toast.success('Operation successful');
      dispatch(resetSuccess());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleViewDetails = (ticket) => {
    dispatch(fetchTicketById(ticket._id));
    setIsDetailOpen(true);
  };

  const handleAddResponse = () => {
    if (!responseText.trim()) return;
    dispatch(addResponse({ ticketId: currentTicket._id, data: { message: responseText } }));
    setResponseText('');
  };

  const handleUpdateStatus = (status) => {
    dispatch(updateTicketStatus({ ticketId: currentTicket._id, data: { status } }));
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open': return 'badge-warning';
      case 'in-progress': return 'badge-info';
      case 'resolved': return 'badge-success';
      case 'closed': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'urgent': return 'badge-danger';
      case 'high': return 'badge-warning';
      case 'medium': return 'badge-info';
      case 'low': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const columns = [
    { header: 'Ticket #', render: (row) => row.ticketNumber },
    { header: 'User', render: (row) => row.userId?.fullName || row.userId?.mobile || 'N/A' },
    { header: 'Subject', render: (row) => row.subject },
    {
      header: 'Category',
      render: (row) => <span style={{ textTransform: 'capitalize' }}>{row.category?.replace('-', ' ')}</span>,
    },
    {
      header: 'Priority',
      render: (row) => (
        <span className={`badge ${getPriorityBadgeClass(row.priority)}`} style={{ textTransform: 'capitalize' }}>
          {row.priority}
        </span>
      ),
    },
    {
      header: 'Status',
      render: (row) => (
        <span className={`badge ${getStatusBadgeClass(row.status)}`} style={{ textTransform: 'capitalize' }}>
          {row.status?.replace('-', ' ')}
        </span>
      ),
    },
    { header: 'Created', render: (row) => formatDate(row.createdAt) },
    {
      header: 'Actions',
      render: (row) => (
        <button className="btn btn-secondary btn-icon" onClick={() => handleViewDetails(row)}>
          <FiEye />
        </button>
      ),
    },
  ];

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div className="card">
            <div className="card-body">
              <h4>{stats.totalTickets || 0}</h4>
              <p>Total Tickets</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <h4 style={{ color: '#f59e0b' }}>{stats.byStatus?.open || 0}</h4>
              <p>Open</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <h4 style={{ color: '#3b82f6' }}>{stats.byStatus?.['in-progress'] || 0}</h4>
              <p>In Progress</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <h4 style={{ color: '#22c55e' }}>{stats.byStatus?.resolved || 0}</h4>
              <p>Resolved</p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <h3 className="card-title">Support Tickets ({totalTickets})</h3>
            <SearchBox value={searchTerm} onChange={setSearchTerm} placeholder="Search..." />
            <select
              className="form-control form-select"
              style={{ width: '150px' }}
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            >
              <option value="">All Status</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s} style={{ textTransform: 'capitalize' }}>
                  {s.replace('-', ' ')}
                </option>
              ))}
            </select>
            <select
              className="form-control form-select"
              style={{ width: '150px' }}
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }}
            >
              <option value="">All Categories</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c} style={{ textTransform: 'capitalize' }}>
                  {c.replace('-', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <DataTable columns={columns} data={tickets} isLoading={isLoading} />
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      {/* Ticket Detail Modal */}
      <Modal
        isOpen={isDetailOpen}
        onClose={() => { setIsDetailOpen(false); dispatch(clearCurrentTicket()); }}
        title={`Ticket: ${currentTicket?.ticketNumber || ''}`}
        size="large"
      >
        {currentTicket && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <strong>User:</strong> {currentTicket.userId?.fullName || currentTicket.userId?.mobile}<br />
              <strong>Subject:</strong> {currentTicket.subject}<br />
              <strong>Category:</strong> <span style={{ textTransform: 'capitalize' }}>{currentTicket.category?.replace('-', ' ')}</span><br />
              <strong>Priority:</strong> <span className={`badge ${getPriorityBadgeClass(currentTicket.priority)}`}>{currentTicket.priority}</span><br />
              <strong>Status:</strong>{' '}
              <select
                className="form-control form-select"
                style={{ width: '150px', display: 'inline-block' }}
                value={currentTicket.status}
                onChange={(e) => handleUpdateStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.replace('-', ' ')}</option>
                ))}
              </select>
            </div>

            <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
              <strong>Description:</strong>
              <p style={{ whiteSpace: 'pre-wrap', marginTop: '8px' }}>{currentTicket.description}</p>
            </div>

            {/* Admin Responses */}
            {currentTicket.adminResponses?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Responses:</strong>
                {currentTicket.adminResponses.map((resp, idx) => (
                  <div key={idx} style={{ background: '#e0f2fe', padding: '12px', borderRadius: '8px', marginTop: '8px' }}>
                    <small style={{ color: '#6b7280' }}>
                      {resp.adminId?.fullName || 'Admin'} - {formatDate(resp.respondedAt)}
                    </small>
                    <p style={{ margin: '4px 0 0' }}>{resp.message}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Add Response */}
            <div className="form-group">
              <label>Add Response</label>
              <textarea
                className="form-control"
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                rows={3}
                placeholder="Type your response..."
              />
              <button
                className="btn btn-primary"
                style={{ marginTop: '8px' }}
                onClick={handleAddResponse}
                disabled={isLoading || !responseText.trim()}
              >
                <FiMessageSquare /> Send Response
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default HelpSupport;
